import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  IconButton,
  Chip,
  Divider,
  CircularProgress,
  Button,
} from '@mui/material';
import {
  NotificationsNone as NotificationsNoneIcon,
  Close as CloseIcon,
  FlightTakeoff as FlightTakeoffIcon,
  People as PeopleIcon,
  PhotoCamera as PhotoCameraIcon,
  Share as ShareIcon,
  ArrowBack as ArrowBackIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useNudges, Nudge, AdminBroadcast, LifecycleNotification } from '../hooks/useNudges';
import { useDismissedNudges } from '../hooks/useDismissedNudges';
import { nudgeKeyFor } from '../utils/api';
import { getCategoryMeta } from '../utils/notificationCategories';
import { clickNotification, dismissNotification, dismissLifecycleNotification } from '../utils/api';

// ─── Nudge icon map ──────────────────────────────────────────────────────────
const nudgeIcon = (category: Nudge['category']) => {
  switch (category) {
    case 'friends': return <PeopleIcon sx={{ fontSize: '1.3rem' }} />;
    case 'photos':  return <PhotoCameraIcon sx={{ fontSize: '1.3rem' }} />;
    case 'share':   return <ShareIcon sx={{ fontSize: '1.3rem' }} />;
    default:        return <NotificationsNoneIcon sx={{ fontSize: '1.3rem' }} />;
  }
};

const nudgeColor: Record<Nudge['category'], string> = {
  friends:  '#4A6FA5',
  photos:   '#C17B3E',
  share:    '#2D6E6A',
  bookings: '#7A8C6E',
};

// ─── Relative time ───────────────────────────────────────────────────────────
function relativeTime(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

// ─── Component ───────────────────────────────────────────────────────────────
const NotificationsPage: React.FC = () => {
  const navigate = useNavigate();
  const {
    nudges,
    admin_broadcasts,
    lifecycle_notifications,
    loading,
    dismissBroadcast,
    dismissLifecycle,
  } = useNudges();
  const { isDismissed, dismiss: dismissNudge } = useDismissedNudges('notifications_page');

  const [dismissing, setDismissing] = useState<string | null>(null);

  const totalCount =
    nudges.filter(n => !isDismissed(nudgeKeyFor(n.category, n.tripId))).length +
    admin_broadcasts.length +
    lifecycle_notifications.length;

  // ── Handlers ────────────────────────────────────────────────────────────
  const handleBroadcastClick = async (n: AdminBroadcast) => {
    await clickNotification(n.notification_id);
    if (n.deep_link) navigate(n.deep_link);
  };

  const handleBroadcastDismiss = async (e: React.MouseEvent, n: AdminBroadcast) => {
    e.stopPropagation();
    setDismissing(n.notification_id);
    await dismissNotification(n.notification_id);
    dismissBroadcast(n.notification_id);
    setDismissing(null);
  };

  const handleLifecycleClick = async (n: LifecycleNotification) => {
    await clickNotification(n.notification_id);
    if (n.deep_link) navigate(n.deep_link);
    else if (n.trip_id) navigate(`/trips/${n.trip_id}`);
  };

  const handleLifecycleDismiss = async (e: React.MouseEvent, n: LifecycleNotification) => {
    e.stopPropagation();
    setDismissing(n.notification_id);
    await dismissLifecycleNotification(n.notification_id);
    dismissLifecycle(n.notification_id);
    setDismissing(null);
  };

  const handleNudgeClick = (n: Nudge) => {
    if (n.tripId) navigate(`/trips/${n.tripId}`);
    else if (n.category === 'friends') navigate('/friends');
    else if (n.category === 'photos') navigate('/photos');
    else if (n.category === 'share') navigate('/trips');
  };

  const handleNudgeDismiss = (e: React.MouseEvent, n: Nudge) => {
    e.stopPropagation();
    dismissNudge(nudgeKeyFor(n.category, n.tripId));
  };

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', pb: 8 }}>
      <Container maxWidth="sm" sx={{ pt: 3 }}>

        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
          <IconButton onClick={() => navigate(-1)} size="small" sx={{ color: 'text.secondary' }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary', flex: 1 }}>
            Notifications
          </Typography>
          {totalCount > 0 && (
            <Chip
              label={`${totalCount} new`}
              size="small"
              sx={{ bgcolor: '#2D6E6A', color: 'white', fontWeight: 600 }}
            />
          )}
        </Box>

        {/* Loading */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress color="primary" />
          </Box>
        )}

        {/* Empty state */}
        {!loading && totalCount === 0 && (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <CheckCircleOutlineIcon sx={{ fontSize: 56, color: '#2D6E6A', mb: 2, opacity: 0.7 }} />
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary', mb: 1 }}>
              You're all caught up!
            </Typography>
            <Typography sx={{ color: 'text.secondary', fontSize: '0.95rem' }}>
              No new notifications right now.
            </Typography>
            <Button
              variant="outlined"
              sx={{ mt: 3, borderColor: '#2D6E6A', color: '#2D6E6A' }}
              onClick={() => navigate('/trips')}
            >
              View My Trips
            </Button>
          </Box>
        )}

        {/* ── Announcements (admin broadcasts) ── */}
        {!loading && admin_broadcasts.length > 0 && (
          <Box sx={{ mb: 4 }}>
            <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 600, letterSpacing: 1 }}>
              Announcements
            </Typography>
            <Divider sx={{ mb: 2, mt: 0.5 }} />
            {admin_broadcasts.map((n) => {
              const meta = getCategoryMeta(n.category);
              return (
                <Card
                  key={n.notification_id}
                  onClick={() => handleBroadcastClick(n)}
                  sx={{
                    mb: 2,
                    borderRadius: '14px',
                    borderLeft: `4px solid ${meta.color}`,
                    boxShadow: '0 2px 12px rgba(0,0,0,0.07)',
                    cursor: n.deep_link ? 'pointer' : 'default',
                    transition: 'all 0.2s',
                    '&:hover': n.deep_link ? { transform: 'translateY(-2px)', boxShadow: '0 6px 20px rgba(0,0,0,0.1)' } : {},
                  }}
                >
                  <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                      <Box sx={{ color: meta.color, mt: 0.3 }}>
                        <NotificationsNoneIcon sx={{ fontSize: '1.3rem' }} />
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Chip
                            label={meta.label}
                            size="small"
                            sx={{ bgcolor: meta.color + '18', color: meta.color, fontWeight: 600, fontSize: '0.7rem', height: 20 }}
                          />
                          <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                            {relativeTime(n.sent_at)}
                          </Typography>
                        </Box>
                        <Typography sx={{ fontSize: '0.9rem', color: 'text.primary', lineHeight: 1.5 }}>
                          {n.message}
                        </Typography>
                      </Box>
                      {meta.dismissable && (
                        <IconButton
                          size="small"
                          onClick={(e) => handleBroadcastDismiss(e, n)}
                          disabled={dismissing === n.notification_id}
                          sx={{ color: 'text.secondary', p: 0.5 }}
                        >
                          <CloseIcon sx={{ fontSize: '1rem' }} />
                        </IconButton>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              );
            })}
          </Box>
        )}

        {/* ── Travel Reminders (lifecycle) ── */}
        {!loading && lifecycle_notifications.length > 0 && (
          <Box sx={{ mb: 4 }}>
            <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 600, letterSpacing: 1 }}>
              Travel Reminders
            </Typography>
            <Divider sx={{ mb: 2, mt: 0.5 }} />
            {lifecycle_notifications.map((n) => (
              <Card
                key={n.notification_id}
                onClick={() => handleLifecycleClick(n)}
                sx={{
                  mb: 2,
                  borderRadius: '14px',
                  borderLeft: '4px solid #2D6E6A',
                  boxShadow: '0 2px 12px rgba(0,0,0,0.07)',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 6px 20px rgba(0,0,0,0.1)' },
                }}
              >
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                    <Box sx={{ color: '#2D6E6A', mt: 0.3 }}>
                      <FlightTakeoffIcon sx={{ fontSize: '1.3rem' }} />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mb: 0.5 }}>
                        {relativeTime(n.sent_at)}
                      </Typography>
                      <Typography sx={{ fontSize: '0.9rem', color: 'text.primary', lineHeight: 1.5 }}>
                        {n.message}
                      </Typography>
                    </Box>
                    <IconButton
                      size="small"
                      onClick={(e) => handleLifecycleDismiss(e, n)}
                      disabled={dismissing === n.notification_id}
                      sx={{ color: 'text.secondary', p: 0.5 }}
                    >
                      <CloseIcon sx={{ fontSize: '1rem' }} />
                    </IconButton>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        )}

        {/* ── Trip nudges ── */}
        {!loading && nudges.filter(n => !isDismissed(nudgeKeyFor(n.category, n.tripId))).length > 0 && (
          <Box sx={{ mb: 4 }}>
            <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 600, letterSpacing: 1 }}>
              Suggestions
            </Typography>
            <Divider sx={{ mb: 2, mt: 0.5 }} />
            {nudges
              .filter(n => !isDismissed(nudgeKeyFor(n.category, n.tripId)))
              .map((n, idx) => {
                const color = nudgeColor[n.category] || '#2D6E6A';
                return (
                  <Card
                    key={idx}
                    onClick={() => handleNudgeClick(n)}
                    sx={{
                      mb: 2,
                      borderRadius: '14px',
                      borderLeft: `4px solid ${color}`,
                      boxShadow: '0 2px 12px rgba(0,0,0,0.07)',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 6px 20px rgba(0,0,0,0.1)' },
                    }}
                  >
                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                        <Box sx={{ color, mt: 0.3 }}>
                          {nudgeIcon(n.category)}
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          {n.tripTitle && (
                            <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mb: 0.5, fontWeight: 600 }}>
                              {n.tripTitle}
                            </Typography>
                          )}
                          <Typography sx={{ fontSize: '0.9rem', color: 'text.primary', lineHeight: 1.5 }}>
                            {n.message}
                          </Typography>
                        </Box>
                        <IconButton
                          size="small"
                          onClick={(e) => handleNudgeDismiss(e, n)}
                          sx={{ color: 'text.secondary', p: 0.5 }}
                        >
                          <CloseIcon sx={{ fontSize: '1rem' }} />
                        </IconButton>
                      </Box>
                    </CardContent>
                  </Card>
                );
              })}
          </Box>
        )}

      </Container>
    </Box>
  );
};

export default NotificationsPage;
